import React from 'react';
import { LuPlus } from 'react-icons/lu';
import {
    StanFloatingMenu,
    type StanFloatingMenuHandle,
} from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { StanFilterConfig, StanFilterValue } from '../types';
import styles from '../StanFilters.module.css';
import { cn } from '@/lib/utils/cn';

/**
 * Common props for all filter components
 */
export interface BaseFilterProps {
    id: string;
    label: string;
    filterItem: StanFilterConfig;
    onApply: (values: StanFilterValue[]) => void;
    onClearFilter?: (id: string) => void;
    disabled?: boolean;
    placement?: 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end';
}

/**
 * Props for the BaseFilter component
 */
interface BaseFilterComponentProps extends BaseFilterProps {
    menuRef: React.RefObject<StanFloatingMenuHandle | null>;
    triggerProps: any;
    summary: string;
    hasSummary: boolean;
    isOpen: boolean;
    handleOpenChange: (isOpen: boolean) => void;
    clearFilters: (event?: React.MouseEvent) => void;
    menuClassName?: string;
    children: React.ReactNode;
}

/**
 * Base filter component that provides common structure for all filter types
 * Reduces code duplication across TextFilter, NumberFilter, DateFilter, and SelectFilter
 */
export const BaseFilter: React.FC<BaseFilterComponentProps> = ({
    id,
    label,
    disabled,
    menuRef,
    triggerProps,
    summary,
    hasSummary,
    isOpen,
    handleOpenChange,
    clearFilters,
    menuClassName,
    placement = 'bottom',
    children,
}) => {
    return (
        <div className='relative inline-block'>
            <StanFloatingMenu
                id={`${id}-menu`}
                name={`${id}-menu`}
                ref={menuRef}
                rounded
                placement={placement}
                className={menuClassName}
                onOpenChange={handleOpenChange}
                trigger={
                    <button
                        id={id}
                        type='button'
                        disabled={disabled}
                        {...triggerProps}
                        className={cn(
                            'duration-fast inline-flex items-center rounded-full py-2 pr-4 pl-3 text-sm font-semibold transition-all',
                            'hover:cursor-pointer',
                            disabled && 'pointer-events-none cursor-not-allowed opacity-60',
                            hasSummary ? styles.chipButtonActive : styles.chipButtonInactive,
                            isOpen && styles.chipButtonFocused
                        )}
                    >
                        <div>
                            <LuPlus
                                className={cn(
                                    'duration-normal mr-2 h-4 w-4 transition-transform',
                                    hasSummary ? 'text-bg-canvas rotate-45' : 'text-brand-main'
                                )}
                                width='16'
                                height='16'
                                onClick={event => {
                                    if (hasSummary) {
                                        clearFilters(event);
                                    }
                                }}
                                aria-label='Clear filter'
                            />
                        </div>
                        <span>{label}</span>
                        {hasSummary && (
                            <div className='ml-1 inline-flex items-center gap-1'>
                                <div className='text-bg-canvas mx-1'>|</div>
                                <div className='text-bg-canvas max-w-[150px] truncate' title={summary}>
                                    {summary}
                                </div>
                            </div>
                        )}
                    </button>
                }
            >
                {children}
            </StanFloatingMenu>
        </div>
    );
};
